import { msg, str } from "@lit/localize";
import { IUser } from "../models/usermodel";
import { localeService } from "./locale-service";

function anyImageParams(image?: string) {
  if (!image) {
    return {
      type: 'icon',
      img: 'account_circle',
    };
  } else if (image.startsWith('http') || image.startsWith('data')) {
    return {
      type: 'img',
      img: image,
    };
  } else {
    // this is mostly to cover for data migration with test data, sigh
    return {
      type: 'img',
      img: ['data:image/webp;base64', image].join(','),
    };
  }
}

function profileImageParams(user?: IUser) {
  return anyImageParams(user?.img);
}

//   (grp1  )              (grp3                  )      (grp6  )
//                    (grp2)(grp4  )    (grp5  )
const seriesMatcher: RegExp = /(?:([0-9]+)\s*x\s*)?(\+?)(([0-9]+)(?:-([0-9]+))?)(?:\s*(s|min))?/gd;

export type SeriesModel = {
  type: 'single' | 'multiple' | 'error';
  desc: string;
  unit?: 's' | 'min';
  noSeries: number;
  noRepetitions: readonly [number | undefined, number];
}
const seriesParser = (text: string | null) => {
  const result: SeriesModel[] = [];
  if (text) {
    let match;
    while ((match = seriesMatcher.exec(text)) !== null) {
      if (match[1] && match[3] && match[4] && !match[5]) {
        result.push({
          type: 'multiple',
          desc: `${match[1]}x${match[3]}${match[6] ?? ''}`,
          unit: (match[6] as 's' | 'min' | undefined),
          noSeries: Number(match[1]),
          noRepetitions: [undefined, Number(match[3])],
        });
      } else if (match[1] && match[3] && match[4] && match[5]) {
        result.push({
          type: 'multiple',
          desc: `${match[1]}x${match[3]}${match[6] ?? ''}`,
          unit: (match[6] as 's' | 'min' | undefined),
          noSeries: Number(match[1]),
          noRepetitions: [Number(match[4]), Number(match[5])],
        });
      } else if (match[3] && match[4] && !match[5]) {
        result.push({
          type: 'single',
          desc: (match[2] ?? '') + match[4] + (match[5] ?? '') + (match[6] ?? ''),
          unit: (match[6] as 's' | 'min' | undefined),
          noSeries: 1,
          noRepetitions: [undefined, Number(match[4])],
        });
      } else if (match[3] && match[4] && match[5]) {
        result.push({
          type: 'single',
          desc: (match[2] ?? '') + match[4] + (match[5] ?? '') + (match[6] ?? ''),
          unit: (match[6] as 's' | 'min' | undefined),
          noSeries: 1,
          noRepetitions: [Number(match[4]), Number(match[5])],
        });
      } else {
        result.push({
          type: 'error',
          desc: match[0],
          noSeries: 0,
          noRepetitions: [undefined, 0],
        })
      }
    }

    // remember to reset the lastIndex for next use
    seriesMatcher.lastIndex = 0;
  }

  return result;
}

export const renderTime = (date: Date, options: Intl.DateTimeFormatOptions = {
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
}) => {
  const now = Date.now();
  const diff = now - date.getTime();
  if (diff < 3 * 60 * 1000) {
    return msg('now');
  } else if (diff < 60 * 60 * 1000) {
    const min = Math.floor(diff / (60 * 1000));
    return msg(str`${min}m ago`);
  } else if (diff < 24 * 60 * 60 * 1000) {
    const hrs = Math.floor(diff / (60 * 60 * 1000));
    return msg(str`${hrs}h ago`);
  } else if (diff < 30 * 24 * 60 * 60 * 1000) {
    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    return msg(str`${days}d ago`);
  } else {
    return Intl.DateTimeFormat(localeService.getLocale(), options).format(date);
  }
}

export const renderTimeDiff = (start: Date, end: Date) => {
  const diff = end.getTime() - start.getTime();
  if (diff % 7 * 24 * 60 * 60 * 1000 == 0) {
    const weeks = Math.floor(diff / (7 * 24 * 60 * 60 * 1000));
    return msg(str`${weeks}weeks`);
  } else if (diff % 24 * 60 * 60 * 1000 == 0) {
    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    return msg(str`${days}days`);
  } else {
    const hrs = Math.floor(diff / (60 * 60 * 1000));
    return msg(str`${hrs}hrs`);
  }
}




export {
  anyImageParams,
  profileImageParams,
  seriesParser,
}
